// src/com/veh_route/VehRoute.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {ContentConainer, ContentHeader, ContentBody} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { vehRouteListRepo, userInfoRepo, comInfoListRepo, rtdTermPosListRepo } from '../../value/AppModel';
import { LIST_VEH_ROUTE } from './VehRouteGql';
import { VehRouteDictionary } from './VehRouteDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import { LIST_RTD_TERM_POS } from '../rtd_term_pos/RtdTermPosGql';
import { RtdTermPosDictionary } from '../rtd_term_pos/RtdTermPosDict';
import VzmSelector from '../../common/VzmSelector';
import DurationPicker from '../../common/DurationPicker';
import ValueMisc from '../../value/ValueMisc';
import VzmIntField from '../../common/VzmIntField';

const ColumnContainer = styled(Box)({
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
});
const ControlBox = styled(Box)({flexGrow:1, display:'flex', alignItems:'center', minHeight:40, boxSizing:'border-box'});

const CompanyLabel = styled(Box)({
    display:'flex', alignItems:'center',
    border: AppStyle.BorderCCC, borderRadius:5, padding:10,
    minHeight:40, boxSizing:'border-box',
    fontWeight:'bold'
});
const VehicleLabel = styled('div')({
    padding:5, border: AppStyle.BorderCCC, borderRadius:5, marginRight:15,
    alignSelf:'stretch', display:'flex', alignItems:'center', 
});


const EmptyDuration = {from:null, till:null};
/**
 * (VEH_ROUTE) 정보 View Controller
 * 경로데이터
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-04 12:42:49 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function VehRoute({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    //const [vehPos, setVehPos] = useState(null);
    const [duration, setDuration] = useState(EmptyDuration);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const rtdTermPosListData = useReactiveVar(rtdTermPosListRepo);
    const [comInfoListOptions, setComInfoListOptions] = useState([{comId:AppWord.ALL, comName:AppWord.AllKorean}]);
    const [vehicleList, setVehicleList] = useState([]); // filtered from rtdTermPosListData
    const [comId, setComId] = useState(AppWord.ALL);
    const [vehicleSelected, setVehicleSelected] = useState({});
    const [changed, setChanged] = useState(false);
    const [vehicleGotRoute, setVehicleGotRoute] = useState({});
    const [speedFilter, setSpeedFilter] = useState(0);
    const vehRouteListData = useReactiveVar(vehRouteListRepo);
    const addAlert = useAlertSetter();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
		onError: (error) => addAlert({title: '운송사목록 조회 오류', message: [error.message]})
    });
    const [getVehRouteList, responseRouteList] = useLazyQuery(LIST_VEH_ROUTE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehRouteDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getRtdTermPosList, responsePosList] = useLazyQuery(LIST_RTD_TERM_POS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetRtdTermPosList(data, option)},
		onError: (error) => addAlert({title: "차량목록조회 오류", message: [error.message]})
    });

    const getVehRouteListNow = () => {
        if(vehicleSelected.vehInfoSeq && duration.from && duration.till) {
            // Check if from and till are in order
            if(duration.from >= duration.till) {
                addAlert({title: '시간설정 오류', message: ['시작시간이 종료시간보다 같거나 늦습니다.']});
                return;
            }
            getVehRouteList({
                variables: {vehInfoSeq: vehicleSelected.vehInfoSeq, timeFrom: duration.from, timeTill: duration.till}
            });
        }
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        let diff;
        if(!sessionInfo.isTransCompanyUser()) {
            diff = new Date().getTime()/1000 - comInfoListData.when;
            if(diff > 600) {
                getComInfoList();
            }
        }
        diff = new Date().getTime()/1000 - rtdTermPosListData.when;
        if(diff > 60) {
            getRtdTermPosList();
        }
    }, []);

    useEffect(()=>{
        if(vehicleSelected.vehInfoSeq) {
            if(vehicleSelected.gpsTime) {
                if(vehicleSelected.gpsTime === AppWord.NOT_REAL_GPS_TIME) {
                    setDuration(EmptyDuration);
                    setChanged(false);
                }
                else {
                    const djs = dayjs(vehicleSelected.gpsTime);
                    const date = djs.toDate();
                    const hour = date.getHours();
                    if(hour > 12) date.setHours(hour-12);
                    else if(hour <= 6) {
                        date.setTime(date.getTime() - 6*3600*1000);
                    }
                    else {
                        date.setHours(0);
                        date.setMinutes(0);
                        date.setSeconds(0);
                    }
                    const from = ValueMisc.getYmdHms(date);
                    setDuration({from:from, till: vehicleSelected.gpsTime});
                    setChanged(true);
                }
            }
            else setDuration(EmptyDuration);
        }
        else setDuration(EmptyDuration);
    }, [vehicleSelected]);

    useEffect(()=>{
        // add AppWord.ALL option
        const options
        = comInfoListData.records.filter((rec)=>rec.dcCd === sessionInfo.user.dscIdAs);
        options.unshift({comId:AppWord.ALL, comName:AppWord.AllKorean});
        setComInfoListOptions(options);
    }, [comInfoListData, sessionInfo]);

    useEffect(()=>{
        if(comId === AppWord.ALL) {
            setVehicleList(rtdTermPosListData.records);
        } else {
            setVehicleList(rtdTermPosListData.records.filter((rec)=>rec.comId === comId));
        }
    }, [rtdTermPosListData, comId]);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<

    const onCompleteGetComList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetRtdTermPosList = (data, clientOption) => {
        if(data.rtdTermPosList) {
            rtdTermPosListRepo({when:new Date().getTime()/1000, records:data.rtdTermPosList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.vehRouteList) {
            vehRouteListRepo({when:new Date().getTime()/1000, records:data.vehRouteList});
            setVehicleGotRoute({...vehicleSelected});
            setChanged(false);
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeComId = (id, value) => {
        setComId(value);
    };

    const onChangeFrom = (from) => {
        const dur = {...duration};
        dur.from = from;
        setDuration(dur);
        setChanged(true);
    };

    const onChangeTill = (till) => {
        const dur = {...duration};
        dur.till = till;
        setDuration(dur);
        setChanged(true);
    };

    const onSelectVehicle = (row) => {
        setVehicleSelected(row);
        vehRouteListRepo({when:0, records:[]});
    };

    const loadingAny
    = responseComList.loading || responsePosList.loading || responseRouteList.loading;

    const renderVehicleListBox = () => {
        return(
            <ColumnContainer width={400} style={{paddingRight:10}}>
                <ContentHeader>
                    {
                        sessionInfo.isTransCompanyUser()
                        ?
                        <CompanyLabel>
                            {sessionInfo.user.dscName}
                        </CompanyLabel>
                        :
                        <ControlBox>
                            <VzmSelector
                                dict={{
                                    id: 'comId', label: '운송사', labelKey: 'comName',
                                }}
                                value={comId}
                                onChange={onChangeComId}
                                fullWidth={true}
                                list={comInfoListOptions}
                            />
                        </ControlBox>
                    }
                </ContentHeader>
                <FlexyTable
                    name={RtdTermPosDictionary.tablePosAsVehicleProps.tableId}
                    tableProps={RtdTermPosDictionary.tablePosAsVehicleProps}
                    records={vehicleList}
                    tools={[]}
                    onRowClicked={onSelectVehicle}
                />
            </ColumnContainer>
        );
    };

    const renderRouteBox = () => {
        return(
            <ColumnContainer flexGrow={1}>
                <ContentHeader>
                    <ControlBox>
                        {
                            vehicleGotRoute.vehInfoSeq && !changed
                            ?
                            <VehicleLabel style={{color: AppStyle.PrimaryRGB}}>
                                {vehicleGotRoute.vehPlates} 경로
                            </VehicleLabel>
                            :
                            vehicleSelected.vehInfoSeq
                            ?
                            <VehicleLabel style={{color: AppStyle.SecondaryRGB}}>
                                {vehicleSelected.vehPlates}
                            </VehicleLabel>
                            :
                            <span style={{fontSize:'0.8rem', color:'#999', marginRight:10}}>
                                차량을 선택하세요.
                            </span>
                        }
                        {
                            vehicleSelected.vehInfoSeq &&
                            <>
                            <DurationPicker
                                from={duration.from} onChangeFrom={onChangeFrom}
                                till={duration.till} onChangeTill={onChangeTill}
                            />
                            <Button
                                variant={AppStyle.mui.contained}
                                onClick={getVehRouteListNow}
                                disabled={!changed || loadingAny}
                                style={{marginLeft:10, marginRight:10}}
                            >
                                조회
                            </Button>
                            <VzmIntField
                                dict={{id:'limit', label:'차량속도(시속km)',}}
                                value={speedFilter}
                                onChange={(id, value)=>setSpeedFilter(value)}
                                style={{width:120}}
                            />
                            </>
                        }
                    </ControlBox>
                </ContentHeader>
                <FlexyTable
                    name={VehRouteDictionary.tableProps.tableId}
                    tableProps={VehRouteDictionary.tableProps}
                    records={vehRouteListData.records.filter((rec)=>rec.speed >= speedFilter)}
                    tools={[]}
                />
            </ColumnContainer>
        );
    };

    return(
        <ContentConainer>
            <ContentBody style={{display:'flex'}}>
                {renderVehicleListBox()}
                {renderRouteBox()}
            </ContentBody>
        </ContentConainer>
    );
}
